import {useQueryClient} from '@tanstack/react-query';
import {useLocation} from '@tanstack/react-router';
import isequal from 'lodash.isequal';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {LabelInstancesQueryKeys} from '~api/label-instances.queries';
import {splitImageOrderBy} from '~utils/miscUtils';
import {
  imagesOverviewViewModeQueryParam,
  itemsPerPageQueryParam,
  listViewGroupByQueryParam,
  Route,
  useQueryParam,
} from '~utils/routeUtil';
import {
  ImageFilters,
  LabelInstance,
  ListViewGroupBy,
  PaginationAction,
  PaginationActionName,
  PaginationActions,
  ViewMode,
} from '~redux/types/images';
import {imageDataActions, selectItemsPerPage} from '~redux/reducers/imageReducer';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {useImageFilters} from '~components/images/image-overview/imageOverview.hooks';
import {ImageOverviewMode} from '~components/images/utils/image-mode-mixins';
import {useLabelItems} from '~components/models/model.hooks';

export interface LabelInstancesContextValue {
  currentPage: number;
  handlePageChange: (action: PaginationActionName, targetPage: number) => void;
  handleResetPage: () => void;
  currentPagination?: PaginationAction;
  setAvailablePaginationActions: Dispatch<SetStateAction<PaginationActions | undefined>>;
  currentTotalLabelInstances?: number;
  setCurrentTotalLabelInstances: Dispatch<SetStateAction<number | undefined>>;
  currentActualTotalLabelInstances?: number;
  setCurrentActualTotalLabelInstances: Dispatch<SetStateAction<number | undefined>>;
  activeLabelInstance?: LabelInstance;
  handleSetActiveLabelInstance: (labelInstance: LabelInstance) => void;
  activeViewMode?: ViewMode;
  setActiveViewMode: Dispatch<SetStateAction<ViewMode | undefined>>;
  isPageDirty: boolean;
  setIsPageDirty: Dispatch<SetStateAction<boolean>>;
}

export const LabelInstancesContext = createContext<LabelInstancesContextValue>({} as LabelInstancesContextValue);

export const useLabelInstances = () => {
  const context = useContext(LabelInstancesContext);
  if (!context) {
    throw new Error('useLabelInstancesState must be used within a LabelInstancesProvider');
  }
  return context;
};

export function LabelInstancesProvider({
  children,
  mode = 'model-defect-book',
}: {
  children: ReactNode;
  mode?: ImageOverviewMode;
}) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId);
  const itemsPerPage = useAppSelector(selectItemsPerPage);
  const [viewMode, setViewMode] = useQueryParam(imagesOverviewViewModeQueryParam);
  const [listViewGroupByParam, setListViewGroupByParam] = useQueryParam(listViewGroupByQueryParam);
  const [queryItemsPerPage, _setQueryItemsPerPage] = useQueryParam(itemsPerPageQueryParam);
  const areViewModesAvailable = [
    Route.modelSamples,
    Route.modelSamplesImage,
    Route.modelSamplesSmartSelectResults,
    Route.modelFinetuneImproveDataset,
    Route.archive,
    Route.archiveImage,
  ].includes(location.pathname as Route);

  const [currentPage, setCurrentPage] = useState(1);
  const [availablePaginationActions, setAvailablePaginationActions] = useState<PaginationActions | undefined>();
  const [currentPagination, setCurrentPagination] = useState<PaginationAction | undefined>();
  const [currentTotalLabelInstances, setCurrentTotalLabelInstances] = useState<number | undefined>();
  const [currentActualTotalLabelInstances, setCurrentActualTotalLabelInstances] = useState<number | undefined>();
  const [activeLabelInstance, setActiveLabelInstance] = useState<LabelInstance | undefined>();
  const [activeViewMode, setActiveViewMode] = useState<ViewMode | undefined>(viewMode as ViewMode);
  const modelLabelItems = useLabelItems({from: 'current-model'});
  const [currentFilters, setCurrentFilters] = useState<ImageFilters>();
  const [imageFilters] = useImageFilters(mode, modelLabelItems);
  const [isPageDirty, setIsPageDirty] = useState(false);

  useEffect(() => {
    if (!isequal(currentFilters, imageFilters)) {
      setCurrentFilters(imageFilters);
      setCurrentPagination(undefined);
      setCurrentPage(1);
    }
  }, [currentFilters, imageFilters]);

  useEffect(() => {
    if (!areViewModesAvailable || (viewMode as ViewMode) === 'image') {
      setActiveLabelInstance(undefined);
    }
  }, [areViewModesAvailable, viewMode]);

  useEffect(() => {
    const defaultViewMode = 'image';
    const isArchiveRoute =
      (location.pathname as Route) === Route.archive || (location.pathname as Route) === Route.archiveImage;

    // For archive routes, default to 'image' view when the param is missing,
    // regardless of the previous route's active view mode.
    if (areViewModesAvailable && isArchiveRoute && !viewMode) {
      setViewMode(defaultViewMode);
      setActiveViewMode(defaultViewMode);
      return;
    }

    if (areViewModesAvailable && !viewMode && !activeViewMode) {
      setViewMode(defaultViewMode);
      setActiveViewMode(defaultViewMode);
    } else if (areViewModesAvailable && viewMode === 'list' && !listViewGroupByParam) {
      setListViewGroupByParam(ListViewGroupBy.SERIAL_NUMBER);
    } else if (areViewModesAvailable) {
      setViewMode(activeViewMode || defaultViewMode);
      setActiveViewMode(activeViewMode || defaultViewMode);
    } else {
      setActiveViewMode(undefined);
    }
  }, [
    activeViewMode,
    areViewModesAvailable,
    setViewMode,
    viewMode,
    location.pathname,
    listViewGroupByParam,
    setListViewGroupByParam,
  ]);

  useEffect(() => {
    if (isPageDirty) {
      const payload = {
        filters: imageFilters,
        pagination: {
          ...splitImageOrderBy(imageFilters?.orderBy),
          action: currentPagination,
          count: itemsPerPage,
        },
      };
      queryClient.invalidateQueries({
        queryKey: LabelInstancesQueryKeys.labelInstancesDataFilterPaginated(
          projectId,
          payload.filters,
          {
            sortBy: payload.pagination?.sortBy,
            sortOrder: payload.pagination?.sortOrder,
            count: payload.pagination?.count,
          },
          currentPage,
          mode,
        ),
      });
      setIsPageDirty(false);
    }
  }, [
    currentFilters,
    currentPage,
    currentPagination,
    imageFilters,
    isPageDirty,
    projectId,
    queryClient,
    mode,
    itemsPerPage,
  ]);

  useEffect(() => {
    if (queryItemsPerPage) {
      const perPageValue = parseInt(queryItemsPerPage as string, 10);
      if ([50, 100, 200, 500].includes(perPageValue) && perPageValue !== itemsPerPage) {
        dispatch(imageDataActions.setItemsPerPage(perPageValue as any));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetPage = () => {
    setCurrentPage(1);
    setCurrentPagination(undefined);
  };

  const handlePageChange = useCallback(
    async (action: PaginationActionName, targetPage: number) => {
      const paginationAction = availablePaginationActions ? availablePaginationActions[action] : undefined;
      if (paginationAction) {
        const pageOffset = action === PaginationActionName.JUMP ? targetPage - currentPage : undefined;
        setCurrentPagination({...paginationAction, pageOffset});
        setCurrentPage(targetPage);
      }
    },
    [availablePaginationActions, currentPage],
  );

  const handleSetActiveLabelInstance = (labelInstance: LabelInstance) => {
    setActiveLabelInstance(labelInstance);
  };

  const value: LabelInstancesContextValue = useMemo(
    () => ({
      currentPage,
      handlePageChange,
      currentPagination,
      setAvailablePaginationActions,
      handleResetPage,
      currentTotalLabelInstances,
      setCurrentTotalLabelInstances,
      currentActualTotalLabelInstances,
      setCurrentActualTotalLabelInstances,
      activeLabelInstance,
      handleSetActiveLabelInstance,
      activeViewMode,
      setActiveViewMode,
      isPageDirty,
      setIsPageDirty,
    }),
    [
      activeLabelInstance,
      activeViewMode,
      currentPage,
      currentPagination,
      currentActualTotalLabelInstances,
      currentTotalLabelInstances,
      handlePageChange,
      isPageDirty,
    ],
  );

  return <LabelInstancesContext.Provider value={value}>{children}</LabelInstancesContext.Provider>;
}
